<script setup lang="js">
import { onMounted, ref, computed, watch } from 'vue';
import { useProjectStore } from '@/stores/project.store';
import { useInputFieldStore } from '@/stores/inputField.store';
import { useNodeStore } from '@/stores/node.store';
import { useRouteParams } from '@vueuse/router';
import ModalTemplateSide from '@/components/modals/ModalTemplateSide.vue';
import BasicToggleSwitch from '@/components/toggle-switch.vue';
import useModal from '@/stores/modal.store';
import { storeToRefs } from 'pinia';
import Multiselect from 'vue-multiselect';
import { useValidationRuleStore } from '@/stores/validationRule.store';
import _ from 'lodash';

const inputFieldStore = useInputFieldStore();
const projectStore = useProjectStore();
const nodeStore = useNodeStore();
const validationRuleStore = useValidationRuleStore();

const { bpNode } = storeToRefs(nodeStore);
const { rules } = storeToRefs(validationRuleStore);
const { project } = storeToRefs(projectStore);
const { typeList, createInputFieldRequest } = storeToRefs(inputFieldStore);

const projectId = useRouteParams('projectId');
const bpId = useRouteParams('bpId');
const nodeId = useRouteParams('nodeId');
const { closeModal } = useModal();

const childDirections = bpNode.value.child_directions;

const title = ref({});
const type = ref('');
const required = ref(false);
const time = ref(false);
const settings = ref(null);
const defaultValue = ref(null);
const order = ref(0);
const child_directions = ref([]);
const selectedChildNodeIds = computed(() => {
  return child_directions.value.map((d) => d.next_bp_node_id);
});

const validationRules = ref([]);

const formFieldType = {
  number: validationRuleStore.NUMBER_FIELD,
  datetime: validationRuleStore.DATE_FIELD,
  text: validationRuleStore.TEXT_FIELD,
  boolean: validationRuleStore.BOOL_FIELD,
  identificator: validationRuleStore.IDENTIFICATOR_FIELD
};

const availableValidationRules = computed(() => {
  if (!type.value) {
    return [];
  }

  return rules.value.filter((r) => r.form_type === formFieldType[type.value]);
});

const mask = ref('');
const maskLazyMode = ref(false);
const maskPlaceholder = ref('_');
const maskDefinitions = ref([['', '']]);
const maskTest = computed(() => ({
  mask: mask.value,
  definitions: _.chain(maskDefinitions.value)
    .fromPairs()
    .mapValues((v) => {
      try {
        return new RegExp(v);
      } catch {
        return /./;
      }
    })
    .value(),
  placeholderChar: maskPlaceholder.value,
  lazy: maskLazyMode.value
}));

onMounted(() => {
  inputFieldStore.clearRequestVariables();
  if (!project.locales) {
    projectStore.getProject(projectId.value);
  }
});

watch(type, () => {
  validationRules.value = validationRules.value.filter((r) => r.form_type === formFieldType[type.value])
})

function createInputField() {
  settings.value = {};

  if (defaultValue.value) {
    settings.value.defaultValue = defaultValue.value;
  }

  if (time.value) {
    settings.value.time = time.value;
  }

  if (validationRules.value.length) {
    settings.value.validationRules = validationRules.value;
  }

  if (type.value == 'text' && mask.value) {
    settings.value.mask = {
      mask: mask.value,
      lazy: maskLazyMode.value,
      placeholder: maskPlaceholder.value,
      definitions: maskDefinitions.value
    };
  }

  inputFieldStore
    .createInputField(
      projectId.value,
      bpId.value,
      nodeId.value,
      title.value,
      type.value,
      required.value,
      settings.value,
      selectedChildNodeIds.value
    )
    .then(() => {
      closeModal();
    });
}

function getName(direction) {
  return direction.next_bp_node.current.node.name;
}
function isErrorField(fieldName) {
  if (createInputFieldRequest.value.error?.errors) {
    return createInputFieldRequest.value.error.errors[fieldName] || false;
  }
}
function isNotFieldError() {
  if (!createInputFieldRequest.value.error?.errors) {
    return createInputFieldRequest.value.error;
  }
}

function addMaskDefinition() {
  maskDefinitions.value.push(['', '']);
}

function deleteMaskDefinition(definitionIndex) {
  maskDefinitions.value.splice(definitionIndex, 1);
}

</script>

<template>
  <ModalTemplateSide @submit="createInputField" :disableButtons="createInputFieldRequest.isLoading">
    <template v-slot:title>Добавить поле для заполнения</template>
    <template v-slot:body>
      <div class="mt-3" v-for="locale in project.locales" :key="locale.id">
        <label class="form-label">Наименование поля({{ locale.name }})</label>
        <input type="text" v-model="title[locale.id]" class="form-control" :class="{ error: isErrorField('title') }" />
      </div>
      <p v-if="isErrorField('title')" class="error-mesage">
        <span v-for="error in isErrorField('title')">{{ error }}</span>
      </p>
      <div class="mt-3">
        <label class="form-label">Тип</label>
        <select v-model="type" class="form-select" :class="{ error: isErrorField('type') }">
          <option v-for="typeKey in Object.keys(typeList)" :key="typeKey" :value="typeKey">
            {{ typeList[typeKey] }}
          </option>
        </select>
        <p v-if="isErrorField('type')" class="error-mesage">
          <span v-for="error in isErrorField('type')">{{ error }}</span>
        </p>
      </div>

      <div class="mt-3">
        <label class="form-label">Кастомные валидации</label>
        <Multiselect v-model="validationRules" :options="availableValidationRules" placeholder="Выбрать..."
          :multiple="true" label="name" track-by="name" class="multiselect-style" />
      </div>

      <template v-if="type === 'text'">
        <div class="mt-2">
          <label class="form-label">Маска</label>
          <input type="text" class="form-control" v-model="mask" />
        </div>
        <div class="mt-2">
          <div class="accordion accordion-flush" id="accordionFlushExample">
            <div class="accordion-item border border-info">
              <h2 class="accordion-header" id="flush-headingOne">
                <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                  data-bs-target="#flush-collapseOne" aria-expanded="false" aria-controls="flush-collapseOne">
                  Дополнительные настройки маски
                </button>
              </h2>
              <div id="flush-collapseOne" class="accordion-collapse collapse" aria-labelledby="flush-headingOne"
                data-bs-parent="#accordionFlushExample">
                <div class="accordion-body">
                  <div class="mb-2">
                    <label class="form-label">Тестирование маски (значение не сохраняется)</label>
                    <input v-mask="maskTest" type="text" class="form-control" />
                  </div>
                  <div class="mb-2 d-flex align-items-center justify-content-between">
                    <label class="form-label">Скрыть символы маски</label>
                    <basic-toggle-switch v-model="maskLazyMode" />
                  </div>
                  <div class="mb-2" v-if="!maskLazyMode">
                    <label class="form-label">Символ заменитель маски</label>
                    <input type="text" v-mask="{ mask: '*', lazy: true }" class="form-control" :value="maskPlaceholder"
                      @complete="maskPlaceholder = $event.detail.value" />
                  </div>
                  <p class="mt-4">Дополнительные символы</p>
                  <div v-for="(def, index) in maskDefinitions" class="mb-2 d-flex justify-content-between" :key="def[0]">
                    <div class="col-2">
                      <input type="text" class="form-control" v-model="def[0]" />
                    </div>
                    <div class="col-8">
                      <input type="text" class="form-control" v-model="def[1]" />
                    </div>
                    <button class="btn btn-primary col-2" :disabled="maskDefinitions.length <= 1" @click="deleteMaskDefinition(index)">
                      Удалить
                    </button>
                  </div>
                  <button class="btn btn-primary w-100" @click="addMaskDefinition">Добавить символ</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>


      <template v-if="type">
        <div class="mt-3">
          <label class="form-label">Значение по умолчанию</label>
          <input type="text" class="form-control" v-model="defaultValue" v-if="type == 'text'" />
          <input type="number" class="form-control" v-model="defaultValue"
            v-if="type == 'number' || type == 'identificator'" />
          <input type="datetime-local" class="form-control" v-model="defaultValue" v-if="type == 'datetime'" />
          <div v-if="type == 'datetime'" class="mt-3 d-flex align-items-center justify-content-between">
            <label class="form-label">Время</label>
            <basic-toggle-switch v-model="time" />
          </div>
          <select v-model="type" class="form-select" v-if="type == 'boolean'">
            <option :value="true">Истина</option>
            <option :value="false">Ложь</option>
          </select>
          <p v-if="isErrorField('defaultValue')" class="error-mesage">
            <span v-for="error in isErrorField('defaultValue')">{{ error }}</span>
          </p>
        </div>
      </template>

      <div class="mt-3">
        <label class="form-label">Дочерние узлы</label>
        <Multiselect v-model="child_directions" :options="childDirections" placeholder="Выбрать..." :multiple="true"
          :custom-label="getName" track-by="next_bp_node_id" class="multiselect-style"
          :class="{ error: isErrorField('child_node_ids') }" />
        <p v-if="isErrorField('child_node_ids')" class="error-mesage">
          <span v-for="error in isErrorField('child_node_ids')">{{ error }}</span>
        </p>
      </div>
      <!-- <div class="mt-3">
        <label class="form-label">Порядок</label>
        <input type="number" v-model="order" class="form-control" min="0"/>
      </div> -->
      <div class="mt-3 d-flex align-items-center justify-content-between">
        <label class="form-label">Обязательность</label>
        <basic-toggle-switch v-model="required" />
      </div>
      <p v-if="isNotFieldError()" class="error-mesage">
        {{ isNotFieldError() }}
      </p>
    </template>
    <template v-slot:submit>Создать</template>
  </ModalTemplateSide>
</template>
